
import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Divider, FormControlLabel, FormGroup,
  Typography, Card, CardContent, TextField, Stack, Chip,
  Drawer, IconButton, useMediaQuery, useTheme, InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  PlayCircleFilled as PlayIcon,
  Article as ArticleIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
  BookmarkRemove as RemoveBookmarkIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  BookmarkBorder as BookmarkIcon,
} from "@mui/icons-material";
import TopBar from "./topbar";
import Footer from "./footer";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 4;

const TYPE_CONFIG = {
  video:      { icon: PlayIcon,    color: "#F57E47", bg: "#FFF4EE", label: "Video" },
  article:    { icon: ArticleIcon, color: "#00AEEF", bg: "#EFF8FF", label: "Article" },
  template:   { icon: FileIcon,    color: "#7C3AED", bg: "#F3EEFF", label: "Template" },
  "case-study": { icon: FileIcon,  color: "#6B7A8D", bg: "#F3F6FA", label: "Case Study" },
};

// ── Filter panel (shared by desktop sidebar + mobile Drawer) ──────────────────
function FilterPanel({ filters, onToggle }) {
  const filterItems = [
    { key: "articles",    label: "Articles" },
    { key: "videos",      label: "Videos" },
    { key: "templates",   label: "Templates" },
    { key: "caseStudies", label: "Case Studies" },
  ];

  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8A9BB0", letterSpacing: 1, textTransform: "uppercase", mb: 1.5 }}>
        Resource Type
      </Typography>
      <FormGroup>
        {filterItems.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={filters[key]}
                onChange={() => onToggle(key)}
                size="small"
                sx={{ color: "#C5D0DC", "&.Mui-checked": { color: "#00AEEF" } }}
              />
            }
            label={<Typography sx={{ fontSize: 13, color: "#3D4B5C", fontWeight: filters[key] ? 700 : 400 }}>{label}</Typography>}
            sx={{ mb: 0.5 }}
          />
        ))}
      </FormGroup>
    </Box>
  );
}

// ── Bookmark card ─────────────────────────────────────────────────────────────
function BookmarkCard({ resource, onView, onRemove }) {
  const config = TYPE_CONFIG[resource.type] || TYPE_CONFIG["case-study"];
  const IconComp = config.icon;

  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: "1px solid #E8EDF2",
        boxShadow: "none",
        mb: 2,
        overflow: "hidden",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": { boxShadow: "0 6px 24px rgba(0,130,200,0.09)", transform: "translateY(-1px)" },
      }}
    >
      {/* Accent bar */}
      <Box sx={{ height: 4, bgcolor: config.color, opacity: 0.8 }} />

      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Icon bubble */}
          <Box
            sx={{
              width: 52, height: 52, borderRadius: "12px",
              bgcolor: config.bg, display: "flex", alignItems: "center",
              justifyContent: "center", flexShrink: 0,
            }}
          >
            <IconComp sx={{ fontSize: 26, color: config.color }} />
          </Box>

          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap" gap={0.5}>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, color: "#0D1117", lineHeight: 1.3 }}>
                {resource.title}
              </Typography>
              <Chip
                label={config.label}
                size="small"
                sx={{
                  height: 18, fontSize: 10, fontWeight: 700,
                  bgcolor: config.bg, color: config.color,
                  border: "none", "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Stack>
            <Typography sx={{
              fontSize: 12, color: "#6B7A8D", lineHeight: 1.6,
              display: "-webkit-box", WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
              {resource.description}
            </Typography>
          </Box>
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={1} mt={2}>
          <Button
            startIcon={<ViewIcon sx={{ fontSize: 15 }} />}
            onClick={onView}
            sx={{
              fontSize: 12, fontWeight: 700, textTransform: "none",
              color: "#00AEEF", bgcolor: "#EFF8FF", borderRadius: "8px",
              px: 1.75, py: 0.6,
              "&:hover": { bgcolor: "#D9F1FC" },
            }}
          >
            View
          </Button>
          <Button
            startIcon={<RemoveBookmarkIcon sx={{ fontSize: 15 }} />}
            onClick={onRemove}
            sx={{
              fontSize: 12, fontWeight: 700, textTransform: "none",
              color: "#EF4444", bgcolor: "#FEF2F2", borderRadius: "8px",
              px: 1.75, py: 0.6,
              "&:hover": { bgcolor: "#FEE2E2" },
            }}
          >
            Remove
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Bookmarks() {
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [bookmarks, setBookmarks] = useState([]);
  const [visible, setVisible] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    articles: false, videos: false, templates: false, caseStudies: false,
  });

  const toggleFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
    setCurrentPage(1);
  };

  useEffect(() => {
    async function loadBookmarks() {
      if (!userId) { setBookmarks([]); return; }
      try {
        const res = await fetch(`https://backend-global-1vq9.onrender.com/api/bookmarks/${userId}`);
        const data = await res.json();
        if (!Array.isArray(data)) { setBookmarks([]); return; }
        setBookmarks(
          data
            .filter((item) => item.resourceId && typeof item.resourceId === "object")
            .map((item) => item.resourceId)
        );
      } catch (err) {
        console.error("Error loading bookmarks:", err);
        setBookmarks([]);
      }
    }
    loadBookmarks();
  }, [userId]);

  useEffect(() => {
    const typeMap = { articles: "article", videos: "video", templates: "template", caseStudies: "case-study" };
    const selected = Object.keys(typeMap).filter((k) => filters[k]);

    let filtered = bookmarks.filter((r) => {
      const matchesSearch = !search.trim() || r.title?.toLowerCase().includes(search.toLowerCase());
      const matchesType = selected.length === 0 || selected.some((k) => typeMap[k] === r.type);
      return matchesSearch && matchesType;
    });

    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    setVisible(filtered.slice(start, start + ITEMS_PER_PAGE));
  }, [bookmarks, search, filters, currentPage]);

  const typeMap = { articles: "article", videos: "video", templates: "template", caseStudies: "case-study" };
  const selected = Object.keys(typeMap).filter((k) => filters[k]);
  const filteredTotal = bookmarks.filter((r) => {
    const matchesSearch = !search.trim() || r.title?.toLowerCase().includes(search.toLowerCase());
    const matchesType = selected.length === 0 || selected.some((k) => typeMap[k] === r.type);
    return matchesSearch && matchesType;
  }).length;

  const totalPages = Math.ceil(filteredTotal / ITEMS_PER_PAGE);
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  const handleRemove = async (resourceId) => {
    await fetch("https://backend-global-w8me.onrender.com/api/bookmarks/remove", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, resourceId }),
    });
    setBookmarks((prev) => prev.filter((r) => r._id !== resourceId));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#F3F6FA" }}>
      <TopBar />

      {/* Mobile filter drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { width: 270, p: 2.5 } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography fontWeight={700} fontSize={16} color="#0D1117">Filter Bookmarks</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2.5 }} />
        <FilterPanel filters={filters} onToggle={toggleFilter} />

        {activeFiltersCount > 0 && (
          <Button
            fullWidth
            onClick={() => setFilters({ articles: false, videos: false, templates: false, caseStudies: false })}
            sx={{ mt: 3, textTransform: "none", color: "#EF4444", fontSize: 13, fontWeight: 600, borderRadius: "10px" }}
          >
            Clear all filters
          </Button>
        )}
      </Drawer>

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>

        {/* Page header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Typography fontWeight={800} color="#0D1117" sx={{ fontSize: { xs: 22, md: 30 }, lineHeight: 1.2 }}>
              Bookmarks
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Your saved resources, all in one place
            </Typography>
          </Box>

          {/* Mobile filter button */}
          {isMobile && (
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={() => setFilterDrawerOpen(true)}
              sx={{
                borderRadius: "10px", borderColor: "#00AEEF", color: "#00AEEF",
                fontWeight: 600, fontSize: 13, textTransform: "none",
                ...(activeFiltersCount > 0 && { bgcolor: "#EFF8FF" }),
              }}
            >
              Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
            </Button>
          )}
        </Stack>

        {/* Search */}
        <TextField
          placeholder="Search bookmarks..."
          fullWidth
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#B0BEC5" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px", fontSize: 14, bgcolor: "#fff",
              "& fieldset": { borderColor: "#E8EDF2" },
              "&:hover fieldset": { borderColor: "#00AEEF" },
              "&.Mui-focused fieldset": { borderColor: "#00AEEF" },
            },
          }}
        />

        {/* Two-column layout */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

          {/* Bookmark list */}
          <Box flex={1} minWidth={0}>
            {/* Count + active filter chips on mobile */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Typography fontSize={13} color="text.secondary">
                {filteredTotal} bookmark{filteredTotal !== 1 ? "s" : ""}
              </Typography>
              {isMobile && activeFiltersCount > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5}>
                  {Object.entries(filters).filter(([, v]) => v).map(([key]) => (
                    <Chip
                      key={key}
                      label={{ articles: "Articles", videos: "Videos", templates: "Templates", caseStudies: "Case Studies" }[key]}
                      size="small"
                      onDelete={() => toggleFilter(key)}
                      sx={{ fontSize: 11, height: 22, bgcolor: "#EFF8FF", color: "#00AEEF" }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>

            {visible.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                minHeight={220} bgcolor="#fff" borderRadius="16px" border="1px solid #E8EDF2">
                <BookmarkIcon sx={{ fontSize: 48, color: "#C5D0DC", mb: 1 }} />
                <Typography fontWeight={600} color="text.secondary" fontSize={14}>
                  No bookmarks found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {search || activeFiltersCount > 0 ? "Try adjusting your search or filters" : "Save resources to see them here"}
                </Typography>
              </Box>
            ) : (
              <>
                {visible.map((resource) => (
                  <BookmarkCard
                    key={resource._id}
                    resource={resource}
                    onView={() => navigate(`/resources/${resource._id}`)}
                    onRemove={() => handleRemove(resource._id)}
                  />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mt={3} flexWrap="wrap" gap={2}>
                    <Button
                      variant="outlined"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      sx={{
                        borderRadius: "10px", borderColor: "#00AEEF", color: "#00AEEF",
                        fontWeight: 600, fontSize: 13, textTransform: "none", px: 2.5,
                        "&:hover": { borderColor: "#007FBB", color: "#007FBB" },
                        "&.Mui-disabled": { borderColor: "#DDE2E8", color: "#B0BEC5" },
                      }}
                    >
                      ← Previous
                    </Button>

                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Box
                          key={i}
                          onClick={() => setCurrentPage(i + 1)}
                          sx={{
                            width: i + 1 === currentPage ? 24 : 8, height: 8,
                            borderRadius: "4px",
                            bgcolor: i + 1 === currentPage ? "#00AEEF" : "#C8DAE8",
                            cursor: "pointer", transition: "all 0.22s ease",
                          }}
                        />
                      ))}
                    </Stack>

                    <Button
                      variant="outlined"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      sx={{
                        borderRadius: "10px", borderColor: "#00AEEF", color: "#00AEEF",
                        fontWeight: 600, fontSize: 13, textTransform: "none", px: 2.5,
                        "&:hover": { borderColor: "#007FBB", color: "#007FBB" },
                        "&.Mui-disabled": { borderColor: "#DDE2E8", color: "#B0BEC5" },
                      }}
                    >
                      Next →
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Box>

          {/* Desktop filter sidebar */}
          {!isMobile && (
            <Box
              sx={{
                width: 240, flexShrink: 0, bgcolor: "#fff",
                borderRadius: "16px", border: "1px solid #E8EDF2",
                p: 2.5, position: "sticky", top: 24,
              }}
            >
              <Typography fontWeight={700} fontSize={15} color="#0D1117" mb={2}>
                Filter Bookmarks
              </Typography>
              <Divider sx={{ mb: 2.5 }} />
              <FilterPanel filters={filters} onToggle={toggleFilter} />

              {activeFiltersCount > 0 && (
                <Button
                  fullWidth size="small"
                  onClick={() => setFilters({ articles: false, videos: false, templates: false, caseStudies: false })}
                  sx={{ mt: 2.5, textTransform: "none", color: "#EF4444", fontSize: 12, fontWeight: 600, borderRadius: "8px" }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          )}
        </Stack>
      </Box>

      <Footer />
    </Box>
  );
}